import PropTypes from 'prop-types';
import { forwardRef, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

import { useOverflowDetector } from 'hooks/useOverflowDetector';

import AvomaTooltip from 'components/Common/AvomaTooltip';

/**
 * @typedef {object} TOverflowingTextProps
 * @property {string} [as]
 * @property {import('react').ReactNode} children
 * @property {import('react').ReactNode} [tooltip]
 * @property {string} [tooltipPosition]
 * @property {object} [tooltipStyles]
 */

/** @type {any} */
const OverflowingText = forwardRef(
  (
    {
      as: Tag = 'span',
      children,
      tooltip = '',
      tooltipPosition = 'bottom',
      tooltipStyles = {},
      ...rest
    },
    forwardedRef
  ) => {
    const [ref, isOverflowing] = useOverflowDetector();
    const mergedRef = useCallback(
      node => {
        ref.current = node;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [ref, forwardedRef]
    );
    const { className, ...tagProps } = rest;

    return (
      <AvomaTooltip
        tooltip={isOverflowing ? tooltip ?? children : undefined}
        tooltipPosition={tooltipPosition}
        userStyles={tooltipStyles}
      >
        <Tag
          className={twMerge('truncate', className)}
          ref={mergedRef}
          {...tagProps}
        >
          {children}
        </Tag>
      </AvomaTooltip>
    );
  }
);

OverflowingText.displayName = 'OverflowingText';

OverflowingText.propTypes = {
  as: PropTypes.string,
  children: PropTypes.node.isRequired,
  tooltip: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node)
  ]),
  tooltipPosition: PropTypes.string,
  tooltipStyles: PropTypes.object,
  rest: PropTypes.object
};

export default OverflowingText;
